import React, { useState, useEffect } from 'react';
import { Upload, Download, Star, Scissors, CheckCircle, Mic, Zap, Archive, Loader, Cpu, Monitor, Grid } from '@geist-ui/icons';

// Modern Circular Progress Component
const ModernCircularProgress: React.FC<{
  progress: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
}> = ({ progress, size = 36, strokeWidth = 3, className = 'text-white/20', children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const normalizedProgress = Math.min(100, Math.max(0, progress));
  const offset = circumference - (normalizedProgress / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-white/10"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-500 ease-out ${className}`}
          strokeLinecap="round"
        />
      </svg>
      {children && (
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
};

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  gradientColor: string;
  bgColor: string;
  glowColor: string;
  progress?: number;
  detail?: string;
  hasProgress?: boolean;
}

interface StepData {
  id: number;
  title: string;
  description: string;
  iconType: 'upload' | 'download' | 'mic' | 'zap' | 'scissors' | 'check' | 'monitor' | 'grid';
  color: string;
  gradientColor: string;
  bgColor: string;
  glowColor: string;
  progress?: number;
  detail?: string;
  hasProgress?: boolean;
}

const stepsData: StepData[] = [
  {
    id: 1,
    title: "Connect Your Platform",
    description: "Connect your Twitch, Kick or paste the VOD URL. We'll handle the rest securely.",
    iconType: 'upload',
    color: "text-primary",
    gradientColor: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-500/10",
    glowColor: "shadow-[0_0_30px_8px_hsl(var(--primary)/0.4)]"
  },
  {
    id: 2,
    title: "VODs Auto-Download",
    description: "Your past broadcasts — up to 24 hours long — are automatically fetched and queued for processing.",
    iconType: 'download',
    color: "text-emerald-500",
    gradientColor: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-500/10",
    glowColor: "shadow-[0_0_30px_8px_#22C55E50]",
    hasProgress: true,
    progress: 65,
    detail: "Downloading: 652MB @ 12.5MB/s"
  },
  {
    id: 3,
    title: "Audio Analysis",
    description: "Transcribing audio content for better clip detection and subtitles.",
    iconType: 'mic',
    color: "text-purple-500",
    gradientColor: "from-purple-500 to-violet-500",
    bgColor: "bg-purple-500/10",
    glowColor: "shadow-[0_0_30px_8px_#A855F750]",
    hasProgress: true,
    progress: 30,
    detail: "Processing: 182/600 segments"
  },
  {
    id: 4,
    title: "AI Finds Highlights",
    description: "Our intelligent system scans your content to identify the most engaging moments.",
    iconType: 'zap',
    color: "text-yellow-500",
    gradientColor: "from-yellow-500 to-orange-500",
    bgColor: "bg-yellow-500/10",
    glowColor: "shadow-[0_0_30px_8px_#EAB30850]"
  },
  {
    id: 5,
    title: "Creating Clips",
    description: "Generating high-quality clips from detected highlights.",
    iconType: 'scissors',
    color: "text-pink-500",
    gradientColor: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-500/10",
    glowColor: "shadow-[0_0_30px_8px_#EC489950]",
    hasProgress: true,
    progress: 80,
    detail: "Clip 4/5: Epic Headshot Compilation"
  },
  {
    id: 6,
    title: "Edit in Pro Editor",
    description: "Fine-tune your clips with our professional video editor. Add captions, crop for any platform, apply templates, and preview in real-time.",
    iconType: 'monitor',
    color: "text-violet-500",
    gradientColor: "from-violet-500 to-purple-600",
    bgColor: "bg-violet-500/10",
    glowColor: "shadow-[0_0_30px_8px_#8B5CF650]",
    detail: "Editing: Epic Clutch Moment — Template: Gaming 9:16"
  },
  {
    id: 7,
    title: "Browse Your Gallery",
    description: "All your clips are organized in a beautiful gallery. Preview, re-edit, download, or manage them from one place.",
    iconType: 'grid',
    color: "text-indigo-500",
    gradientColor: "from-indigo-500 to-blue-600",
    bgColor: "bg-indigo-500/10",
    glowColor: "shadow-[0_0_30px_8px_#6366F150]",
    detail: "12 clips • 3 published • 2 drafts"
  },
  {
    id: 8,
    title: "Share & Grow",
    description: "Your clips are ready! Export to TikTok, YouTube Shorts, and more.",
    iconType: 'check',
    color: "text-green-500",
    gradientColor: "from-green-500 to-emerald-500",
    bgColor: "bg-green-500/10",
    glowColor: "shadow-[0_0_30px_8px_#22C55E50]"
  }
];

const HowItWorksDemo: React.FC = () => {
  const [activeStep, setActiveStep] = useState(1); // Start with download step to show progress
  const [isAnimating, setIsAnimating] = useState(true);
  const [simulatedProgress, setSimulatedProgress] = useState<{ [key: number]: number }>({
    1: 65,
    2: 30,
    4: 80
  });

  // Helper function to render icons
  const renderIcon = (iconType: StepData['iconType'], size: number = 24, className?: string) => {
    switch (iconType) {
      case 'upload':
        return <Upload size={size} className={className} />;
      case 'download':
        return <Download size={size} className={className} />;
      case 'mic':
        return <Mic size={size} className={className} />;
      case 'zap':
        return <Zap size={size} className={className} />;
      case 'scissors':
        return <Scissors size={size} className={className} />;
      case 'check':
        return <CheckCircle size={size} className={className} />;
      case 'monitor':
        return <Monitor size={size} className={className} />;
      case 'grid':
        return <Grid size={size} className={className} />;
      default:
        return null;
    }
  };

  // Convert stepsData to steps with rendered icons
  const steps = stepsData.map(step => ({
    ...step,
    icon: renderIcon(step.iconType)
  }));

  useEffect(() => {
    if (!isAnimating) return undefined;

    // Editor step (index 5) gets more time so users can see the mockup
    const duration = activeStep === 5 ? 8000 : 4000;
    const timeout = setTimeout(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, duration);

    return () => clearTimeout(timeout);
  }, [isAnimating, activeStep]);

  // Simulate progress updates for active step
  useEffect(() => {
    if (!steps[activeStep]?.hasProgress) return undefined;

    const progressInterval = setInterval(() => {
      setSimulatedProgress(prev => {
        const currentProgress = prev[activeStep] || 0;
        const newProgress = currentProgress + Math.random() * 5;
        return {
          ...prev,
          [activeStep]: Math.min(95, newProgress)
        };
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [activeStep]);

  const handleStepClick = (index: number) => {
    setActiveStep(index);
    setIsAnimating(false);
  };

  const handleMouseEnter = () => {
    setIsAnimating(false);
  };

  const handleMouseLeave = () => {
    setIsAnimating(true);
  };

  return (
    <div className="w-full max-w-7xl mx-auto py-8 md:py-16 px-4">
      <h3 className="text-2xl md:text-4xl font-bold text-center mb-4 md:mb-6 bg-gradient-to-r from-primary to-[hsl(207_90%_70%)] bg-clip-text text-transparent">
        See Clipoid in Action
      </h3>
      <p className="text-base md:text-xl text-muted-foreground text-center mb-8 md:mb-16 max-w-3xl mx-auto">
        Watch how our AI-powered platform transforms your streams into viral clips
      </p>

      {/* Modern Job-style Card */}
      <div className="relative p-4 md:p-8 rounded-2xl md:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/5 hover:bg-white/[0.04] hover:border-white/10 transition-all duration-300">

        {/* Gradient Overlay */}
        <div
          className={`absolute inset-0 rounded-2xl md:rounded-3xl opacity-5 transition-opacity duration-300 bg-gradient-to-br ${steps[activeStep].gradientColor}`}
        />

        {/* Header with Mock VOD Info */}
        <div className="relative z-10 mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <img
              src="https://images.unsplash.com/photo-1542751371-adc38448a05e?w=320&h=180&fit=crop"
              alt="Stream thumbnail"
              className="w-full sm:w-40 h-32 sm:h-28 object-cover rounded-lg md:rounded-xl border border-white/10"
            />
            <div className="flex-1">
              <h4 className="text-lg md:text-2xl font-semibold text-white/90 mb-2">
                Epic Gaming Session - Road to Radiant
              </h4>
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <span className="px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium backdrop-blur-sm bg-purple-900/60 text-purple-300 border border-purple-400/40">
                  TWITCH
                </span>
                <span className="text-xs md:text-sm text-white/60">Duration: 4h 32m</span>
                <span className="text-xs md:text-sm text-white/60 hidden sm:inline">•</span>
                <span className="text-xs md:text-sm text-white/60">Processing: 12m 45s</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          {steps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            const stepProgress = isActive && step.hasProgress ? simulatedProgress[index] || step.progress : (isCompleted ? 100 : 0);

            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`
                  relative flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-lg md:rounded-xl transition-all duration-300 cursor-pointer
                  ${isActive ? 'bg-white/5 border border-white/10 scale-[1.02]' : 'bg-white/[0.02] hover:bg-white/[0.04]'}
                  ${isCompleted ? 'opacity-70' : ''}
                `}
              >
                {/* Stage Icon with Progress */}
                <div className="relative flex-shrink-0">
                  <ModernCircularProgress
                    progress={stepProgress}
                    size={48}
                    strokeWidth={3}
                    className={
                      isCompleted ? 'text-emerald-500' :
                        isActive ? (
                          step.gradientColor.includes('blue') ? 'text-blue-400' :
                            step.gradientColor.includes('emerald') ? 'text-emerald-400' :
                              step.gradientColor.includes('purple') ? 'text-purple-400' :
                                step.gradientColor.includes('yellow') ? 'text-yellow-400' :
                                  step.gradientColor.includes('pink') ? 'text-pink-400' : 'text-green-400'
                        ) : 'text-white/20'
                    }
                  >
                    {renderIcon(step.iconType, 20,
                      isCompleted ? 'text-emerald-400' :
                        isActive ? 'text-white' : 'text-white/30'
                    )}
                  </ModernCircularProgress>

                  {/* Completion Checkmark */}
                  {isCompleted && (
                    <div className="absolute -top-1 -right-1 w-4 md:w-5 h-4 md:h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={10} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Stage Content */}
                <div className="flex-1 text-left">
                  <h5 className={`text-sm md:text-base font-medium transition-colors ${isActive ? 'text-white' : isCompleted ? 'text-white/70' : 'text-white/50'
                    }`}>
                    {step.title}
                  </h5>
                  {isActive && step.detail && (
                    <p className="text-xs text-white/60 mt-1 hidden sm:block">{step.detail}</p>
                  )}
                </div>

                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute inset-0 rounded-lg md:rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
                )}
              </button>
            );
          })}
        </div>

        {/* Main Progress Bar */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between text-xs md:text-sm text-white/60 mb-2 md:mb-3">
            <span>Overall Progress</span>
            <span className="font-mono font-semibold">{Math.round(((activeStep + 1) / steps.length) * 100)}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-3 md:h-4 overflow-hidden shadow-inner">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out shadow-sm bg-gradient-to-r ${steps[activeStep].gradientColor}`}
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Active Step Details */}
        <div className={`
          p-4 md:p-6 rounded-xl md:rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-md
          transition-all duration-500 ${steps[activeStep].glowColor}
        `}>
          <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
            <div className={`
              p-3 md:p-4 rounded-lg md:rounded-xl bg-gradient-to-br ${steps[activeStep].gradientColor} backdrop-blur-sm
              shadow-lg shadow-black/20 flex-shrink-0
            `}>
              {renderIcon(steps[activeStep].iconType, 32, "text-white")}
            </div>

            <div className="flex-1">
              <h4 className="text-lg md:text-2xl font-semibold text-white mb-2 md:mb-3">
                {steps[activeStep].title}
              </h4>
              <p className="text-sm md:text-lg text-white/70 leading-relaxed mb-3 md:mb-4">
                {steps[activeStep].description}
              </p>

              {/* Progress Details */}
              {steps[activeStep].hasProgress && (
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center justify-between text-xs md:text-sm">
                    <span className="text-white/50">Progress</span>
                    <span className="font-mono text-white/80">
                      {Math.round(simulatedProgress[activeStep] || steps[activeStep].progress || 0)}%
                    </span>
                  </div>

                  {activeStep === 1 && (
                    <div className="grid grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/50">Downloaded</span>
                        <span className="font-mono text-white/70">652MB</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Speed</span>
                        <span className="font-mono text-white/70">12.5MB/s</span>
                      </div>
                    </div>
                  )}

                  {activeStep === 2 && (
                    <div className="grid grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/50">Segments</span>
                        <span className="font-mono text-white/70">182/600</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Duration</span>
                        <span className="font-mono text-white/70">45m</span>
                      </div>
                    </div>
                  )}

                  {activeStep === 4 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-white/50">Clips Created</span>
                        <span className="font-mono text-white/70">4/5</span>
                      </div>
                      <div className="text-xs md:text-sm">
                        <span className="text-white/50">Current:</span>
                        <span className="ml-1 md:ml-2 text-white/70 block sm:inline">Epic Headshot</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Editor Preview */}
              {activeStep === 5 && (
                <div className="mt-3 md:mt-4">
                  <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-violet-500/10">
                    <img
                      src="/assets/editor_mockup.png"
                      alt="Clipoid Professional Video Editor"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-center">
                      <span className="text-xs text-violet-300 font-medium">🎬 Cropping</span>
                    </div>
                    <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                      <span className="text-xs text-purple-300 font-medium">💬 Captions</span>
                    </div>
                    <div className="p-2 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20 text-center">
                      <span className="text-xs text-fuchsia-300 font-medium">📤 Export</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Gallery Preview */}
              {activeStep === 6 && (
                <div className="mt-3 md:mt-4">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 1, title: 'Tarkov Clutch', duration: '0:42s', status: 'published' },
                      { id: 2, title: 'Warzone Snipe', duration: '0:28s', status: 'published' },
                      { id: 3, title: 'Valorant Ace', duration: '0:35s', status: 'published' },
                      { id: 4, title: 'Team Fight', duration: '0:51s', status: 'draft' },
                      { id: 5, title: 'Victory Royale', duration: '0:33s', status: 'new' },
                      { id: 6, title: 'Apex Slide Kill', duration: '0:19s', status: 'new' },
                    ].map((clip) => (
                      <div key={clip.id} className="relative rounded-lg overflow-hidden border border-white/10 aspect-[9/16] group/clip">
                        <img
                          src={`/assets/clip_thumb_${clip.id}.png`}
                          alt={clip.title}
                          className="w-full h-full object-cover group-hover/clip:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        {clip.status === 'published' && (
                          <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-emerald-500/90 rounded text-[8px] text-white font-medium shadow-sm">
                            Published
                          </div>
                        )}
                        {clip.status === 'draft' && (
                          <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-amber-500/90 rounded text-[8px] text-white font-medium shadow-sm">
                            Draft
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-black/50 backdrop-blur-sm">
                          <span className="text-[9px] text-white/90 font-medium block truncate">{clip.title}</span>
                          <span className="text-[8px] text-white/50">{clip.duration}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <span className="text-xs text-indigo-300">📁 12 clips total</span>
                    <div className="flex gap-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">3 published</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">2 drafts</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Completion Message */}
              {activeStep === 7 && (
                <div className="mt-3 md:mt-4 p-3 md:p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-emerald-400 font-medium flex items-center gap-2 text-xs md:text-base">
                    <CheckCircle size={16} className="md:w-5 md:h-5" />
                    5 amazing clips created and ready to share!
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">🎵 TikTok</span>
                    <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">📺 YouTube Shorts</span>
                    <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">📸 Instagram Reels</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Visual Demo Area */}
        <div className="mt-4 md:mt-6 flex items-center justify-center">
          <div className="text-xs text-white/40 flex items-center gap-2">
            <Loader size={12} className="animate-spin" />
            <span className="text-xs">Live demo • Updates every 4s</span>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center mt-8 md:mt-12">
        <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6">
          Ready to transform your streams into viral content?
        </p>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
          <a
            href="/register"
            className="px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl bg-gradient-to-r from-primary to-blue-600 text-white font-semibold hover:shadow-[0_0_30px_5px_hsl(var(--primary)/0.5)] hover:scale-105 transition-all duration-300 text-sm md:text-base"
          >
            Start Creating Now
          </a>
          <a
            href="#features"
            className="px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl border-2 border-white/10 text-white/80 font-semibold hover:bg-white/5 hover:border-white/20 transition-all duration-300 text-sm md:text-base"
          >
            Learn More
          </a>
        </div>
      </div>

    </div>
  );
};

export default HowItWorksDemo;